import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, IconButton, CircularProgress, TextField, Card, CardMedia, CardActions } from '@mui/material';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { AddPhotoAlternate, Delete } from '@mui/icons-material';
import axios from 'axios';
import config from '../config/config';

const ImageAttachmentDialog = ({ open, onClose, taskId }) => {
  const [images, setImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && taskId) {
      fetchImages();
    }
  }, [open, taskId]);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-TenantID': 'admin',
        },
      });
      const taskImages = response.data;

      // Download each image
      const downloadedImages = await Promise.all(taskImages.map(async (taskImage) => {
        const fileName = taskImage.imageUrl.split('/').pop(); // Extract the file name from the URL
        try {
          const downloadResponse = await axios.get(`${config.apiBaseUrl}/api/tasks/${taskId}/downloadImage/${fileName}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'X-TenantID': 'admin',
            },
            responseType: 'blob',
          });

          if (downloadResponse.status === 200) {
            const imageUrl = URL.createObjectURL(downloadResponse.data);
            return { ...taskImage, imageUrl };
          } else {
            console.error(`Failed to download image ${fileName} with status code: ${downloadResponse.status}`);
            return null;
          }
        } catch (downloadError) {
          console.error(`Error downloading image ${fileName}:`, downloadError);
          return null;
        }
      }));

      setImages(downloadedImages.filter(image => image !== null));
    } catch (error) {
      console.error('Error fetching images:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      }
    }
  };

  const handleFileChange = (event) => {
    setSelectedFiles(event.target.files);
  };

  const handleUpload = async () => {
    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('file', selectedFiles[i]);
    }

    try {
      await axios.post(`${config.apiBaseUrl}/api/tasks/${taskId}/uploadImage`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-TenantID': 'admin',
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchImages();
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error uploading images:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axios.delete(`${config.apiBaseUrl}/api/tasks/${taskId}/images/${imageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-TenantID': 'admin',
        },
      });
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      if (error.response) {
        console.error('Error response:', error.response);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Attach Images</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" width="100%">
            <TextField
              label="Selected Files"
              value={Array.from(selectedFiles).map(file => file.name).join(', ')}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-image"
              type="file"
              multiple
              onChange={handleFileChange}
            />
            <label htmlFor="upload-image">
              <IconButton color="primary" component="span">
                <AddPhotoAlternate />
              </IconButton>
            </label>
            {selectedFiles.length > 0 && (
              <Button onClick={handleUpload} color="primary" disabled={uploading} variant="contained" sx={{ ml: 2 }}>
                {uploading ? <CircularProgress size={24} /> : 'Upload'}
              </Button>
            )}
          </Box>
          {images.length > 0 ? (
            <Box mt={4} width="100%">
              <Carousel showThumbs={false} dynamicHeight={false} emulateTouch>
                {images.map((image) => (
                  <Box key={image.imageId} position="relative">
                    <Card>
                      <CardMedia
                        component="img"
                        height="300"
                        image={image.imageUrl}
                        alt={image.description || 'Task Image'}
                        style={{ objectFit: 'contain' }}
                      />
                      <CardActions>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDeleteImage(image.imageId)}
                          style={{ position: 'absolute', top: 10, right: 10, transform: 'translateX(-10px)' }}
                        >
                          <Delete />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Carousel>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" mt={4}>
              No images available
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageAttachmentDialog;